import { Button, Drawer, Text } from '@lmc/design-system'
import { Form as AntdForm, ConfigProvider, type FormInstance } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { type ReactNode, type RefObject, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { StepSection, stepFieldErrors } from './stepLayout'

type StepDrawerProps<Values> = {
  children: ReactNode
  confirmLabel: ReactNode
  form: FormInstance<Values>
  initialValues: Values
  onCancel: () => void
  /** Called with every value in the drawer's form once its fields are valid. */
  onConfirm: (values: Values) => void
  onValuesChange?: (changed: Partial<Values>) => void
  testId: string
  title: ReactNode
}

/** The drawer's own form, laid out like a step. */
const DrawerForm = <Values extends object>({
  children,
  form,
  hostRef,
  initialValues,
  onValuesChange
}: StepDrawerProps<Values> & { hostRef: RefObject<HTMLDivElement> }) => (
  <FieldErrors ref={hostRef}>
    <AntdForm
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
    >
      <StepSection>{children}</StepSection>
    </AntdForm>
  </FieldErrors>
)

/**
 * The primary button, then Cancel: the design system drawer lines them up on
 * the left, clear of the Intercom launcher in the bottom-right corner.
 */
const DrawerActions = <Values extends object>({
  confirmLabel,
  form,
  onCancel,
  onConfirm,
  testId
}: StepDrawerProps<Values>) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <>
      <Button
        data-testid={`${testId}-confirm`}
        onClick={() =>
          form.validateFields().then(
            () => onConfirm(form.getFieldsValue(true) as Values),
            () => undefined
          )
        }
      >
        {confirmLabel}
      </Button>
      <Button
        variant="secondary"
        data-testid={`${testId}-cancel`}
        onClick={onCancel}
      >
        {t(incidentReportsConstants.CASE_V2_DRAWER_CANCEL)}
      </Button>
    </>
  )
}

/** The title in Heading M, as designed, not the drawer's smaller default. */
const DrawerTitle = ({ children }: { children: ReactNode }) => (
  <Text as="span" variant="heading-md" weight="medium">
    {children}
  </Text>
)

/**
 * Adds or edits one entry in its own form, so nothing reaches the step until
 * the entry is valid and confirmed. Render it only while open: each opening
 * starts from `initialValues`.
 *
 * antd opens its popups in the body, which the drawer treats as outside it,
 * so they open inside it instead: a select's beside its field, a modal in the
 * form.
 */
const StepDrawer = <Values extends object>(props: StepDrawerProps<Values>) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const hostRef = useRef<HTMLDivElement>(null)

  return (
    <Drawer
      open
      // A click beside the drawer would discard the entry without asking.
      disablePointerDismissal
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          props.onCancel()
        }
      }}
      title={<DrawerTitle>{props.title}</DrawerTitle>}
      closeLabel={t(incidentReportsConstants.CASE_V2_DRAWER_CLOSE)}
      actions={<DrawerActions {...props} />}
      data-testid={props.testId}
    >
      <ConfigProvider
        getPopupContainer={(trigger) =>
          trigger?.parentElement ?? hostRef.current ?? document.body
        }
      >
        <DrawerForm {...props} hostRef={hostRef} />
      </ConfigProvider>
    </Drawer>
  )
}

const FieldErrors = styled.div`
  ${stepFieldErrors}
`

export default StepDrawer
